import { Menu, Button, Badge } from "antd";
import {
  HouseIcon,
  CarSimpleIcon,
  ChatCircleIcon,
  GearIcon,
  UsersThreeIcon,
  MoneyIcon,
  MapTrifoldIcon,
  RoadHorizonIcon,
  ChartLineIcon,
  NotebookIcon,
} from "@phosphor-icons/react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { clearAuthCookies } from "../../utils/cookieUtils";
import { useTranslation } from "react-i18next";
import logo from "../../assets/img/logo.svg";
import { useEffect, useState, useMemo, useCallback, startTransition } from "react";
import { useSignalR } from "../../Context/ChatContext";
import ROUTES from "../../routes";
import { preloadRoute, preloadCriticalRoutes } from "../../routePreload";

const iconSize = 18;

// Sabit değerler component dışında - her renderda yeniden oluşturulmaz
const PARENT_MENU_KEYS = new Set([
  "users-group",
  "rides-group",
  "operational-metrics-menu",
  "location-management",
  "communication-group",
  "crm-group",
  "payments",
  "subscriptions-group",
  "settings",
  "system-logs"
]);

/** Menü hover'da ilgili route chunk'ını önceden yükler (tıklayınca sayfa hemen açılır) */
const PreloadableLabel = ({ path, children }) => (
  <span onMouseEnter={() => preloadRoute(path)}>{children}</span>
);

// Exact path matching - saf fonksiyon, component dışında tanımlı
const isPathActive = (path, currentPath) => {
  if (currentPath === path) return true;
  if (path.includes(':')) {
    const pathPattern = path.replace(/:[^/]+/g, '[^/]+');
    return new RegExp(`^${pathPattern}$`).test(currentPath);
  }
  return false;
};

const SideBar = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const connection = useSignalR();
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [openKeysState, setOpenKeysState] = useState([]);

  // Get selected keys - SADECE leaf node'ları (children'ı olmayan item'ları) ekle
  // Ant Design best practice: selectedKeys sadece tıklanabilir leaf item'ları içermeli
  // Parent menu item'ları selectedKeys'e eklenmemeli, sadece openKeys'de olmalı
  const selectedKeys = useMemo(() => {
    const keys = [];
    const currentPath = location.pathname;

    // Tüm route'ları kontrol et - exact match veya pattern match
    Object.values(ROUTES).forEach((route) => {
      if (isPathActive(route, currentPath)) {
        keys.push(route);
      }
    });

    // Eğer hiçbir exact match yoksa, current path'i direkt ekle
    if (keys.length === 0) {
      keys.push(currentPath);
    }

    // Parent key'leri filtrele - sadece route path'lerini tut
    // Parent menu key'leri (group key'leri) selectedKeys'e eklenmemeli
    return keys.filter(key => !PARENT_MENU_KEYS.has(key));
  }, [location.pathname]);

  // Get default open keys - nested menüler için parent key'leri açık tut
  const defaultOpenKeys = useMemo(() => {
    const keys = [];
    const currentPath = location.pathname;

    // Nested menü parent key'lerini kontrol et
    if (currentPath.startsWith('/users') || currentPath.startsWith('/authorized-users')) {
      keys.push('users-group');
    }
    if (currentPath.startsWith('/trips') || currentPath.startsWith('/trip-logs')) {
      keys.push('rides-group');
    }
    if (currentPath.startsWith('/operational-metrics')) {
      keys.push('operational-metrics-menu');
    }
    if (currentPath.startsWith('/location/')) {
      keys.push('location-management');
    }
    if (currentPath.startsWith('/communication/')) {
      keys.push('communication-group');
    }
    if (currentPath === ROUTES.CRM || currentPath === ROUTES.CUSTOMER_MESSAGES) {
      keys.push('crm-group');
    }
    if (currentPath.startsWith('/payments/')) {
      keys.push('payments');
    }
    if (currentPath.startsWith('/subscriptions/')) {
      keys.push('subscriptions-group');
    }
    if (currentPath.startsWith('/settings/')) {
      keys.push('settings');
      // System logs nested menu için
      if (currentPath.startsWith('/settings/system-logs/')) {
        keys.push('system-logs');
      }
    }

    return keys;
  }, [location.pathname]);

  // OpenKeys'i location değiştiğinde güncelle
  useEffect(() => {
    setOpenKeysState(defaultOpenKeys);
  }, [defaultOpenKeys]);

  // Sık kullanılan sayfaları arka planda önceden yükle (ilk geçişte gecikmeyi azaltır)
  useEffect(() => {
    preloadCriticalRoutes();
  }, []);


  const handleLogout = useCallback(() => {
    clearAuthCookies();
    navigate(ROUTES.LOGIN);
  }, [navigate]);

  // Fixed: Memory leak - cleanup event listeners
  useEffect(() => {
    if (!connection) return;

    if (location.pathname === ROUTES.COMMUNICATION_CHAT || location.pathname === "/chat") {
      setUnreadMessagesCount(0);
      return;
    }

    const handleReceiveMessage = (_senderId, _message) => {
      setUnreadMessagesCount((prev) => prev + 1);
    };

    connection.on("ReceiveMessage", handleReceiveMessage);

    // Cleanup: Remove event listener when component unmounts or dependencies change
    return () => {
      if (connection) {
        connection.off("ReceiveMessage", handleReceiveMessage);
      }
    };
  }, [connection, location.pathname]);


  // Icon weight helper - selectedKeys'e göre belirle
  const getIconWeight = (path) => {
    return selectedKeys.includes(path) ? "fill" : "duotone";
  };

  // Menu items - useMemo ile optimize edildi
  const menuItems = useMemo(() => [
    {
      label: <PreloadableLabel path={ROUTES.DASHBOARD}>{t("dashboard.pageTitle")}</PreloadableLabel>,
      key: ROUTES.DASHBOARD,
      icon: <HouseIcon size={iconSize} weight={getIconWeight(ROUTES.DASHBOARD)} />,
    },
    {
      label: t("menu.usersAndAuth"),
      key: "users-group",
      icon: <UsersThreeIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.USERS}>{t("users.pageTitle")}</PreloadableLabel>, key: ROUTES.USERS },
        { label: <PreloadableLabel path={ROUTES.AUTHORIZED_USERS}>{t("authorizedUsers.pageTitle")}</PreloadableLabel>, key: ROUTES.AUTHORIZED_USERS },
      ],
    },
    {
      label: <PreloadableLabel path={ROUTES.DRIVERS}>{t("drivers.pageTitle")}</PreloadableLabel>,
      key: ROUTES.DRIVERS,
      icon: <CarSimpleIcon size={iconSize} weight={getIconWeight(ROUTES.DRIVERS)} />,
    },
    {
      label: t("menu.rides"),
      key: "rides-group",
      icon: <RoadHorizonIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.TRIPS}>{t("trips.pageTitle")}</PreloadableLabel>, key: ROUTES.TRIPS },
        { label: <PreloadableLabel path={ROUTES.TRIP_LOGS}>{t("liveRides.pageTitle")}</PreloadableLabel>, key: ROUTES.TRIP_LOGS },
      ],
    },
    {
      label: t("menu.operationalMetrics"),
      key: "operational-metrics-menu",
      icon: <ChartLineIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_OVERVIEW}>{t("menu.overview")}</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_OVERVIEW },
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_DRIVERS}>{t("menu.driverMetrics")}</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_DRIVERS },
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_PASSENGERS}>{t("menu.passengerMetrics")}</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_PASSENGERS },
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_PERFORMANCE}>{t("menu.performanceReport")}</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_PERFORMANCE },
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_COVERAGE}>{t("menu.coverageHeatmap")}</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_COVERAGE },
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_ACCEPTANCE_CANCELLATION}>{t("menu.acceptanceCancellationReport")}</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_ACCEPTANCE_CANCELLATION },
        { label: <PreloadableLabel path={ROUTES.OPERATIONAL_METRICS_DRIVER_PENALTIES}>Sürücü Cezaları</PreloadableLabel>, key: ROUTES.OPERATIONAL_METRICS_DRIVER_PENALTIES },
      ],
    },
    {
      label: t("menu.locationManagement"),
      key: "location-management",
      icon: <MapTrifoldIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.LOCATION_SMART_MAP}>{t("menu.smartMap")}</PreloadableLabel>, key: ROUTES.LOCATION_SMART_MAP },
        { label: <PreloadableLabel path={ROUTES.LOCATION_ACTIVE_DRIVERS}>{t("menu.activeDrivers")}</PreloadableLabel>, key: ROUTES.LOCATION_ACTIVE_DRIVERS },
      ],
    },
    {
      label: t("menu.communication"),
      key: "communication-group",
      icon: <ChatCircleIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.COMMUNICATION_CHAT}><Badge count={unreadMessagesCount} size="small" offset={[-15, 0]}>{t("menu.messages")}</Badge></PreloadableLabel>, key: ROUTES.COMMUNICATION_CHAT },
        { label: <PreloadableLabel path={ROUTES.COMMUNICATION_NOTIFICATIONS}>{t("notifications.pageTitle")}</PreloadableLabel>, key: ROUTES.COMMUNICATION_NOTIFICATIONS },
        { label: <PreloadableLabel path={ROUTES.COMMUNICATION_BULK_SMS}>{t("bulkSms.pageTitle")}</PreloadableLabel>, key: ROUTES.COMMUNICATION_BULK_SMS },
      ],
    },
    {
      label: t("menu.crm") || "CRM",
      key: "crm-group",
      icon: <NotebookIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.CUSTOMER_MESSAGES}>{t("customerMessages.pageTitle") || "Müşteri Mesajları"}</PreloadableLabel>, key: ROUTES.CUSTOMER_MESSAGES },
        { label: <PreloadableLabel path={ROUTES.CRM}>{t("crm.notesAndInterviews") || "Notlar & Görüşmeler"}</PreloadableLabel>, key: ROUTES.CRM },
      ],
    },
    {
      label: t("menu.payments"),
      key: "payments",
      icon: <MoneyIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.PAYMENTS_BANK_TRANSFER}>{t("menu.bankTransferPayments")}</PreloadableLabel>, key: ROUTES.PAYMENTS_BANK_TRANSFER },
      ],
    },
    {
      label: t("menu.subscriptions"),
      key: "subscriptions-group",
      icon: <ChartLineIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.SUBSCRIPTIONS_DRIVERS}>{t("driverSubscriptions.pageTitle")}</PreloadableLabel>, key: ROUTES.SUBSCRIPTIONS_DRIVERS },
        { label: <PreloadableLabel path={ROUTES.SUBSCRIPTIONS_AUDIT_LOGS}>{t("subscriptionAuditLogs.pageTitle")}</PreloadableLabel>, key: ROUTES.SUBSCRIPTIONS_AUDIT_LOGS },
        { label: <PreloadableLabel path={ROUTES.SUBSCRIPTIONS_STATISTICS}>{t("menu.subscriptionStatistics")}</PreloadableLabel>, key: ROUTES.SUBSCRIPTIONS_STATISTICS },
      ],
    },
    {
      label: t("menu.settings"),
      key: "settings",
      icon: <GearIcon size={iconSize} weight="duotone" />,
      children: [
        { label: <PreloadableLabel path={ROUTES.SETTINGS_GENERAL}>{t("settings.generalSettings")}</PreloadableLabel>, key: ROUTES.SETTINGS_GENERAL },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_REGION}>{t("region.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_REGION },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_VEHICLE_TYPES}>{t("vehicletypes.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_VEHICLE_TYPES },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_DISTANCE_DISCOUNTS}>{t("distancediscounts.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_DISTANCE_DISCOUNTS },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_SUBSCRIPTION_PLANS}>{t("subscriptionPlans.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_SUBSCRIPTION_PLANS },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_DRIVER_DISCOUNTS}>{t("driverDiscounts.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_DRIVER_DISCOUNTS },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_AGREEMENTS}>{t("agreements.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_AGREEMENTS },
        {
          label: t("systemLogs"),
          key: "system-logs",
          children: [
            { label: <PreloadableLabel path={ROUTES.SETTINGS_SYSTEM_LOGS_SMS}>{t("smsLogs.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_SYSTEM_LOGS_SMS },
          ],
        },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_FAQ}>{t("faq.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_FAQ },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_PAGES}>{t("pages.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_PAGES },
        { label: <PreloadableLabel path={ROUTES.SETTINGS_BANK_ACCOUNTS}>{t("bankAccounts.pageTitle")}</PreloadableLabel>, key: ROUTES.SETTINGS_BANK_ACCOUNTS },
      ],
    },
  ], [t, selectedKeys, unreadMessagesCount]);

  return (
    <aside
      className="sidenav navbar navbar-vertical navbar-expand-xs border-radius-lg fixed-start ms-3 bg-white my-3"
      id="sidenav-main"
    >
      <div className="sidenav-header">
        <button
          className="fas fa-times p-3 cursor-pointer text-dark opacity-5 position-absolute end-0 top-0 d-xl-none border-0 bg-transparent"
          aria-label="Close sidebar"
          id="iconSidenav"
        />
        <Link className="navbar-brand text-center px-4 py-3 m-0" to="/">
          <h5 className="text-dark">
            <img src={logo} className="navbar-brand-img h-100" alt="Logo" />
          </h5>
        </Link>
      </div>
      <hr className="horizontal dark mt-0 mb-2" />
      <div
        className="collapse navbar-collapse w-auto"
        id="sidenav-collapse-main"
      >
        <Menu
          mode="inline"
          onClick={(e) => {
            // Sadece leaf node'lara (route path'lerine) navigate et
            if (!PARENT_MENU_KEYS.has(e.key)) {
              // startTransition: sayfa geçişi UI'ı bloke etmez (React 18 best practice)
              startTransition(() => navigate(e.key));
            }
          }}
          selectedKeys={selectedKeys}
          openKeys={openKeysState}
          onOpenChange={(keys) => {
            setOpenKeysState(keys);
          }}
          items={menuItems}
          multiple={false}
        />
      </div>
      <div className="sidenav-footer position-absolute w-100 bottom-0">
        <div className="mx-3 mb-3">
          <Button type="primary" onClick={handleLogout} htmlType="submit" block>
            {t("common.logout")}
          </Button>
        </div>
      </div>
    </aside>
  );
};

export default SideBar;
